import { useEffect, useRef, useState } from 'react';
import { Menu, Plus, Pencil, Trash2, History } from 'lucide-react';
import { usePageStore } from '../../stores/page-store';
import { useActivityStore, timeAgo, type Activity } from '../../stores/activity-store';
import { cn } from '../../lib/utils';
import { UserMenu } from './UserMenu';

interface TopBarProps {
  sidebarCollapsed: boolean;
  onMobileMenuOpen: () => void;
}

// ─────────────────────────────────────────────────────────────
// TopBar — single-row header (light).
//   left  → page title (published via page-store)
//   right → page action · recent edits · account menu
// Recent edits sits next to the account menu; both are borderless.
// Grid sticky headers offset by 72 (see grid pages' `top: 72`).
// ─────────────────────────────────────────────────────────────

export function TopBar({ sidebarCollapsed, onMobileMenuOpen }: TopBarProps) {
  const title = usePageStore((s) => s.title);
  const action = usePageStore((s) => s.action);

  return (
    <header
      className={cn(
        'fixed right-0 top-0 z-30 flex items-center gap-4 border-b transition-[left] duration-300',
        sidebarCollapsed ? 'md:left-16' : 'md:left-64',
        'left-0',
      )}
      style={{
        height: 72,
        background: 'var(--color-panel)',
        borderColor: 'var(--color-border)',
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="md:hidden flex h-9 w-9 items-center justify-center rounded"
        style={{ color: 'var(--color-text-mid)' }}
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Left: page title (no breadcrumb) */}
      <div className="flex-1 min-w-0">
        {title && (
          <h1
            className="truncate"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 600,
              letterSpacing: -0.3,
              lineHeight: 1.4,
              color: 'var(--color-text)',
              margin: 0,
              paddingBottom: 1,
            }}
          >
            {title}
          </h1>
        )}
      </div>

      {/* Right: page action · recent edits · account (recent edits kept next to the user) */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {action}

        {/* Divider separating the page action from the recent-edits / account group */}
        {action && (
          <span
            aria-hidden
            style={{ width: 1, height: 26, background: 'var(--color-border)', margin: '0 2px', flexShrink: 0 }}
          />
        )}

        <RecentEdits />
        <UserMenu />
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// RecentEdits — session activity log (recent create/update/delete).
// ─────────────────────────────────────────────────────────────

function RecentEdits() {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const activities = useActivityStore((s) => s.activities);
  const markAllRead = useActivityStore((s) => s.markAllRead);
  const clear = useActivityStore((s) => s.clear);

  useEffect(() => {
    if (!open) return;
    markAllRead();
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || btnRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, markAllRead]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        aria-label="Recent edits"
        title="Recent edits"
        style={{
          width: 36,
          height: 36,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-mid)',
          background: open ? 'var(--color-bg)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          position: 'relative',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'var(--color-bg)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        <History size={16} />
      </button>

      {open && (
        <div
          ref={popRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 360,
            maxHeight: 480,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.2,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                Recent edits
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500, marginTop: 2 }}>
                {activities.length === 0
                  ? 'No activity yet'
                  : `Last ${activities.length} action${activities.length === 1 ? '' : 's'}`}
              </div>
            </div>
            {activities.length > 0 && (
              <button
                onClick={clear}
                style={{
                  fontSize: 11.5,
                  color: 'var(--color-text-dim)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  padding: '4px 6px',
                  borderRadius: 4,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-bad)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-dim)')}
                title="Clear history"
              >
                Clear
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {activities.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
                <History size={28} style={{ margin: '0 auto 8px', opacity: 0.35 }} />
                <div>Edits you make on this device will show up here.</div>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {activities.map((a, i) => (
                  <ActivityItem key={a.id} a={a} isLast={i === activities.length - 1} />
                ))}
              </ul>
            )}
          </div>

          <div
            style={{
              padding: '8px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              fontSize: 10.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              textAlign: 'center',
              letterSpacing: 0.6,
            }}
          >
            SESSION ACTIVITY · STORED LOCALLY
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityItem({ a, isLast }: { a: Activity; isLast: boolean }) {
  const meta = activityMeta(a.type);
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <li
      style={{
        padding: '10px 16px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: meta.bg,
          color: meta.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
        }}
        aria-hidden
      >
        {meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.35 }}>
          <span style={{ color: 'var(--color-text-mid)' }}>{meta.verb}</span>{' '}
          <span style={{ fontWeight: 500 }}>{a.label}</span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--color-text-dim)',
            fontFamily: 'var(--font-mono)',
            marginTop: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{a.entity}</span>
          <span>·</span>
          <span>{timeAgo(a.timestamp)}</span>
        </div>
      </div>
    </li>
  );
}

function activityMeta(type: Activity['type']): { icon: React.ReactNode; verb: string; bg: string; fg: string } {
  switch (type) {
    case 'create':
      return { icon: <Plus size={13} />, verb: 'Created', bg: 'rgba(26,143,90,0.12)', fg: 'var(--color-good)' };
    case 'update':
      return { icon: <Pencil size={13} />, verb: 'Updated', bg: 'rgba(87,154,59,0.10)', fg: 'var(--color-accent)' };
    case 'delete':
      return { icon: <Trash2 size={13} />, verb: 'Deleted', bg: 'rgba(185,28,28,0.10)', fg: 'var(--color-bad)' };
  }
}
